import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { X, Minimize2, Menu } from 'lucide-react';
import { ChatInterface } from './ChatInterface';
import { ChatHistorySidebar } from './ChatHistorySidebar';
import { BookingCTA } from './BookingCTA';
import { isFeatureEnabled } from '@/lib/featureFlags';
import { useAuth } from '@/contexts/AuthContext';
import { useChat } from '@/contexts/ChatContext';
import { getAllConversationsForUser } from '@/data/askCircle';
import circleLogoUrl from '@/assets/circle-network-logo.png';
interface ChatModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  minimized: boolean;
  onMinimizedChange: (minimized: boolean) => void;
  initialQuery?: string;
  onNewChat?: () => void;
  chatKey?: number;
  isNewChat?: boolean;
}
const QUICK_RESPONSES = ["Tell me about your current business challenges", "What's your biggest growth goal right now?", "Show me tools that could help"];
export function ChatModal({
  open,
  onOpenChange,
  minimized,
  onMinimizedChange,
  initialQuery,
  onNewChat,
  chatKey,
  isNewChat
}: ChatModalProps) {
  const {
    user
  } = useAuth();
  const {
    conversations,
    setConversations,
    currentConversationId,
    switchConversation
  } = useChat();
  const [showSidebar, setShowSidebar] = useState(true);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);

  // Refresh conversations list when chat key changes (new conversation created)
  useEffect(() => {
    if (open && user?.id && chatKey >= 0) {
      const loadConversations = async () => {
        try {
          const convs = await getAllConversationsForUser(user.id);
          setConversations(convs);
        } catch (error) {
          console.error('Failed to refresh conversations:', error);
        }
      };
      // Debounce the reload slightly
      const timer = setTimeout(loadConversations, 500);
      return () => clearTimeout(timer);
    }
  }, [chatKey, open, user?.id, setConversations]);
  const handleDeleteConversation = (conversationId: string) => {
    setConversations(conversations.filter(c => c.id !== conversationId));
    // If deleting current conversation, switch to new chat
    if (conversationId === currentConversationId && onNewChat) {
      onNewChat();
    }
  };

  // On mobile, the AskCircleMobileFab handles the minimized button
  // On desktop, show minimized widget in bottom left
  if (minimized || !open && typeof window !== 'undefined' && localStorage.getItem('cn_chat_started') === 'true') {
    return <div className="hidden md:block fixed bottom-4 left-4 z-50">
        <Button onClick={() => onMinimizedChange(false)} size="icon" className="h-14 w-14 rounded-full shadow-lg hover:shadow-xl transition-all bg-primary hover:bg-primary/90">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6 text-black">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </Button>
      </div>;
  }
  return <Dialog open={open && !minimized} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-40px)] md:w-[calc(100vw-80px)] h-[calc(100vh-40px)] md:h-[calc(100vh-80px)] p-0 flex flex-col gap-0 [&>button]:hidden mx-auto rounded-lg">
        <VisuallyHidden>
          <DialogTitle>Circle Agent Concierge Chat</DialogTitle>
          <DialogDescription>AI-powered assistant for real estate agent tools</DialogDescription>
        </VisuallyHidden>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={circleLogoUrl} alt="Circle Network" />
              <AvatarFallback>CN</AvatarFallback>
            </Avatar>
            <div>
              <h3 className="font-semibold text-base">Circle Agent Concierge</h3>
              <p className="text-sm text-muted-foreground">Your AI agent advisor and our live team are a click away. </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => setShowMobileSidebar(true)} className="md:hidden">
              <Menu className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => onMinimizedChange(true)}>
              <Minimize2 className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => onOpenChange(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex flex-1 overflow-hidden min-w-0">
          {/* Sidebar - Desktop only */}
          {showSidebar && <div className="hidden md:block flex-shrink-0">
              <ChatHistorySidebar conversations={conversations} currentConversationId={currentConversationId} onSelectConversation={switchConversation} onNewChat={onNewChat || (() => {})} onDeleteConversation={handleDeleteConversation} />
            </div>}

          {/* Mobile Sidebar Sheet */}
          <Sheet open={showMobileSidebar} onOpenChange={setShowMobileSidebar}>
            <SheetContent side="left" className="p-0 w-80">
              <ChatHistorySidebar conversations={conversations} currentConversationId={currentConversationId} onSelectConversation={id => {
              switchConversation(id);
              setShowMobileSidebar(false);
            }} onNewChat={() => {
              onNewChat?.();
              setShowMobileSidebar(false);
            }} onDeleteConversation={handleDeleteConversation} />
            </SheetContent>
          </Sheet>

          {/* Chat Area */}
          <div className="flex-1 flex flex-col overflow-hidden min-w-0">
            {/* Quick Response Buttons */}
            {!initialQuery && <div className="px-4 py-3 border-b bg-muted/30">
                <div className="flex flex-wrap gap-2">
                  {QUICK_RESPONSES.map(response => <Button key={response} variant="outline" size="sm" className="text-xs" onClick={() => {
                console.log('Quick response:', response);
              }}>
                      {response}
                    </Button>)}
                </div>
              </div>}

            {/* Chat Interface */}
            <div className="flex-1 overflow-hidden min-w-0">
              <ChatInterface key={chatKey} initialMessage={initialQuery} isNewChat={isNewChat} conversationId={currentConversationId} />
            </div>
          </div>
        </div>

        {/* Footer - Book Human Advisor */}
        {isFeatureEnabled('askCircleBooking') && <div className="p-4 border-t bg-muted/30">
            <BookingCTA onBookingClick={() => {
          window.open('https://api.leadconnectorhq.com/widget/booking/oQZkoVPtAyrF1cWrrnPe', '_blank');
        }} isAutoTriggered={false} />
          </div>}
      </DialogContent>
    </Dialog>;
}